/**
 * AddonListTab - Tab de listagem de add-ons com paginação e filtros avançados
 * Exibe tabela com todos os add-ons contratados
 * 
 * @version 1.2
 * @date 2025-12-19
 * @changelog
 *   - v1.2: Integrado AddonDetailsSheet para visualização de detalhes
 *   - v1.1: Adicionada paginação, filtro por provider, melhor UX
 */

import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  Search, 
  MoreHorizontal, 
  ExternalLink, 
  XCircle,
  Eye,
  Plus,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ServerCog,
  Copy
} from 'lucide-react';
import { useAddonManagement, TenantAddonWithDetails, useProvidersList } from '@/hooks/useAddonManagement';
import { AddonDetailsSheet } from './AddonDetailsSheet';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';

// Constantes de paginação
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;

interface AddonListTabProps {
  onCreateClick: () => void;
}

/**
 * Componente para exibir status com badge colorido
 */
function StatusBadge({ status }: { status: string }) {
  const variants: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string }> = {
    active: { variant: 'default', label: 'Ativo' },
    cancelled: { variant: 'destructive', label: 'Cancelado' },
    pending: { variant: 'secondary', label: 'Pendente' },
    expired: { variant: 'outline', label: 'Expirado' },
  };

  const config = variants[status] || { variant: 'outline', label: status };

  return <Badge variant={config.variant}>{config.label}</Badge>;
}

/**
 * Componente de paginação
 */
interface PaginationProps {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

function Pagination({ 
  currentPage, 
  totalPages, 
  pageSize, 
  totalItems, 
  onPageChange, 
  onPageSizeChange 
}: PaginationProps) {
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4">
      {/* Info de itens */}
      <div className="text-sm text-muted-foreground">
        Exibindo {startItem}-{endItem} de {totalItems} registros
      </div>

      <div className="flex items-center gap-4">
        {/* Seletor de tamanho de página */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Por página:</span>
          <Select value={String(pageSize)} onValueChange={(v) => onPageSizeChange(Number(v))}>
            <SelectTrigger className="w-20 h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map(size => (
                <SelectItem key={size} value={String(size)}>{size}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Controles de navegação */}
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onPageChange(1)}
            disabled={currentPage === 1}
          >
            <ChevronsLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          
          <span className="text-sm text-muted-foreground px-2">
            Página {currentPage} de {totalPages || 1}
          </span>
          
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage >= totalPages}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onPageChange(totalPages)}
            disabled={currentPage >= totalPages}
          >
            <ChevronsRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export function AddonListTab({ onCreateClick }: AddonListTabProps) {
  const { t } = useTranslation('admin');
  const { addons, isLoadingAddons, cancelAddon, isCancelling } = useAddonManagement();
  const { data: providers } = useProvidersList();
  
  // Estados de filtro
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [providerFilter, setProviderFilter] = useState<string>('all');
  
  // Estados de paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  
  // Estado do sheet de detalhes
  const [selectedAddon, setSelectedAddon] = useState<TenantAddonWithDetails | null>(null);
  const [showDetailsSheet, setShowDetailsSheet] = useState(false);

  // Filtra add-ons
  const filteredAddons = useMemo(() => {
    return addons.filter(addon => {
      // Filtro de busca
      const searchLower = search.toLowerCase();
      const matchesSearch = !search || 
        addon.tenant?.name?.toLowerCase().includes(searchLower) ||
        addon.plan_addons?.name?.toLowerCase().includes(searchLower) ||
        addon.api_providers?.name?.toLowerCase().includes(searchLower) ||
        addon.stripe_subscription_item_id?.toLowerCase().includes(searchLower);

      // Filtro de status
      const matchesStatus = statusFilter === 'all' || addon.status === statusFilter;

      // Filtro de provider
      const matchesProvider = providerFilter === 'all' || addon.api_providers?.id === providerFilter;

      return matchesSearch && matchesStatus && matchesProvider;
    });
  }, [addons, search, statusFilter, providerFilter]);

  // Calcula paginação
  const totalPages = Math.ceil(filteredAddons.length / pageSize);
  const paginatedAddons = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return filteredAddons.slice(start, end);
  }, [filteredAddons, currentPage, pageSize]);

  // Reset para página 1 quando filtros mudam
  const handleFilterChange = (setter: (value: string) => void) => (value: string) => {
    setter(value);
    setCurrentPage(1);
  };

  // Handler de mudança de tamanho de página
  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  /**
   * Handler para cancelar add-on
   */
  const handleCancel = async (addon: TenantAddonWithDetails) => {
    if (!confirm(`Tem certeza que deseja cancelar o add-on "${addon.plan_addons?.name}" do tenant "${addon.tenant?.name}"?`)) {
      return;
    }

    logger.info('[AddonListTab] Cancelando add-on', `addonId: ${addon.id}, tenantId: ${addon.tenant_id}`);
    await cancelAddon({ tenantAddonId: addon.id, notes: 'Cancelado via painel admin' });
  };

  /**
   * Abre link do Stripe
   */
  const openStripeLink = (subscriptionId: string) => {
    window.open(`https://dashboard.stripe.com/subscriptions/${subscriptionId}`, '_blank');
  };

  /**
   * Copia texto para clipboard
   */
  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiado!`);
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <CardTitle className="text-lg">
            {t('addonManagement.list.title', 'Add-ons Contratados')}
          </CardTitle>
          <Button size="sm" onClick={onCreateClick}>
            <Plus className="h-4 w-4 mr-2" />
            {t('addonManagement.actions.createManual', 'Cadastro Manual')}
          </Button>
        </div>
        
        {/* Filtros */}
        <div className="flex flex-col sm:flex-row gap-2 mt-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t('addonManagement.list.searchPlaceholder', 'Buscar por tenant, add-on, provider ou Stripe ID...')}
              value={search}
              onChange={(e) => handleFilterChange(setSearch)(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={statusFilter} onValueChange={handleFilterChange(setStatusFilter)}>
            <SelectTrigger className="w-full sm:w-36">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos Status</SelectItem>
              <SelectItem value="active">Ativos</SelectItem>
              <SelectItem value="cancelled">Cancelados</SelectItem>
              <SelectItem value="pending">Pendentes</SelectItem>
              <SelectItem value="expired">Expirados</SelectItem>
            </SelectContent>
          </Select>
          <Select value={providerFilter} onValueChange={handleFilterChange(setProviderFilter)}>
            <SelectTrigger className="w-full sm:w-40">
              <SelectValue placeholder="Provider" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos Providers</SelectItem>
              {providers?.map(provider => (
                <SelectItem key={provider.id} value={provider.id}>
                  {provider.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('addonManagement.list.columns.tenant', 'Tenant')}</TableHead>
                <TableHead>{t('addonManagement.list.columns.addon', 'Add-on')}</TableHead>
                <TableHead>{t('addonManagement.list.columns.provider', 'Provider')}</TableHead>
                <TableHead className="text-center">{t('addonManagement.list.columns.qty', 'Qtd')}</TableHead>
                <TableHead>{t('addonManagement.list.columns.status', 'Status')}</TableHead>
                <TableHead>{t('addonManagement.list.columns.stripeId', 'Stripe ID')}</TableHead>
                <TableHead>{t('addonManagement.list.columns.createdAt', 'Criado em')}</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoadingAddons ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    Carregando...
                  </TableCell>
                </TableRow>
              ) : paginatedAddons.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    {filteredAddons.length === 0 && addons.length > 0 
                      ? 'Nenhum add-on encontrado com os filtros aplicados'
                      : 'Nenhum add-on cadastrado'}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedAddons.map((addon) => (
                  <TableRow key={addon.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <span>{addon.tenant?.name || '-'}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-5 w-5 opacity-50 hover:opacity-100"
                          onClick={() => copyToClipboard(addon.tenant_id, 'Tenant ID')}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span>{addon.plan_addons?.name || '-'}</span>
                        {addon.plan_addons?.addon_type && (
                          <Badge variant="outline" className="text-xs">
                            {addon.plan_addons.addon_type}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {addon.api_providers ? (
                        <div className="flex items-center gap-2">
                          <ServerCog className="h-4 w-4 text-muted-foreground" />
                          <span>{addon.api_providers.name}</span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="secondary">{addon.quantity}</Badge>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={addon.status} />
                    </TableCell>
                    <TableCell>
                      {addon.stripe_subscription_item_id ? (
                        <div className="flex items-center gap-1">
                          <span
                            className="text-xs text-muted-foreground flex items-center gap-1 max-w-[140px] truncate"
                            title={addon.stripe_subscription_item_id}
                          >
                            {addon.stripe_subscription_item_id.slice(0, 18)}...
                          </span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5 opacity-50 hover:opacity-100"
                            onClick={() => copyToClipboard(addon.stripe_subscription_item_id!, 'Stripe Item ID')}
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {format(new Date(addon.created_at), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => copyToClipboard(addon.id, 'Add-on ID')}>
                            <Copy className="h-4 w-4 mr-2" />
                            Copiar ID
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => {
                            setSelectedAddon(addon);
                            setShowDetailsSheet(true);
                          }}>
                            <Eye className="h-4 w-4 mr-2" />
                            Ver Detalhes
                          </DropdownMenuItem>
                          {addon.stripe_subscription_item_id && (
                            <DropdownMenuItem onClick={() => copyToClipboard(addon.stripe_subscription_item_id!, 'Stripe Item ID')}>
                              <ExternalLink className="h-4 w-4 mr-2" />
                              Copiar Stripe Item ID
                            </DropdownMenuItem>
                          )}
                          {addon.status === 'active' && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={() => handleCancel(addon)}
                                className="text-destructive focus:text-destructive"
                                disabled={isCancelling}
                              >
                                <XCircle className="h-4 w-4 mr-2" />
                                Cancelar Add-on
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        
        {/* Paginação */}
        {filteredAddons.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            totalItems={filteredAddons.length}
            onPageChange={setCurrentPage}
            onPageSizeChange={handlePageSizeChange}
          />
        )}
      </CardContent>

      {/* Sheet de detalhes */}
      <AddonDetailsSheet
        addon={selectedAddon}
        open={showDetailsSheet}
        onOpenChange={setShowDetailsSheet}
      />
    </Card>
  );
}
